import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Paginated, VisitSummary } from '@foundation/shared';
import {
  LOCATION_FLAG_LABEL_TH,
  VISIT_OUTCOME_LABEL_TH,
  VISIT_STATUS_LABEL_TH,
  VISIT_STATUSES,
  VISIT_TYPE_LABEL_TH,
} from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { Empty, ErrorAlert, Loading } from '../components/ui';

const STATUS_TONE: Record<string, string> = {
  scheduled: 'blue',
  in_progress: 'amber',
  completed: 'green',
  missed: 'red',
  cancelled: '',
  rescheduled: 'amber',
};

export default function VisitList() {
  const [rows, setRows] = useState<VisitSummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const load = useCallback(
    async (append = false, from?: string) => {
      setError(null);
      try {
        const res = await api<Paginated<VisitSummary>>(
          `/visits${buildQuery({
            status,
            flagged: flaggedOnly ? '1' : undefined,
            limit: 50,
            cursor: from,
          })}`,
        );
        setRows((prev) => (append && prev ? [...prev, ...res.data] : res.data));
        setCursor(res.nextCursor);
      } catch (e) {
        setError(e);
        if (!append) setRows([]);
      }
    },
    [status, flaggedOnly],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>งานลงพื้นที่</h1>
          <p>
            ติดตามงานเยี่ยมและตรวจงานที่ตำแหน่งเช็คอินผิดปกติหรือผลการเยี่ยมต้องติดตาม
          </p>
        </div>
      </div>

      <ErrorAlert error={error} />

      <div className="card">
        <div className="toolbar">
          <div className="field">
            <label htmlFor="vstatus">สถานะ</label>
            <select
              id="vstatus"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setFlaggedOnly(false);
              }}
            >
              <option value="">ทั้งหมด</option>
              {VISIT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {VISIT_STATUS_LABEL_TH[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="checkbox" style={{ marginTop: 22 }}>
              <input
                type="checkbox"
                checked={flaggedOnly}
                onChange={(e) => {
                  setFlaggedOnly(e.target.checked);
                  if (e.target.checked) setStatus('');
                }}
              />
              <span>เฉพาะงานที่รอตรวจ</span>
            </label>
          </div>
        </div>

        {rows === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>
            {flaggedOnly ? 'ไม่มีงานที่ต้องตรวจ 🎉' : 'ยังไม่มีงานลงพื้นที่ตามเงื่อนไขนี้'}
          </Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>รหัส</th>
                    <th className="wrap">ผู้รับความช่วยเหลือ</th>
                    <th>ประเภท</th>
                    <th>เจ้าหน้าที่</th>
                    <th>นัด</th>
                    <th>สถานะ</th>
                    <th>ผล</th>
                    <th>ตำแหน่ง</th>
                    <th>รูป</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((v) => (
                    <tr key={v.id}>
                      <td className="mono small">
                        <Link className="row-link" to={`/visits/${v.id}`}>
                          {v.visitCode}
                        </Link>
                      </td>
                      <td className="wrap">{v.beneficiaryName}</td>
                      <td className="small">{VISIT_TYPE_LABEL_TH[v.visitType] ?? v.visitType}</td>
                      <td className="small">{v.staffName}</td>
                      <td className="small muted">{formatDateTime(v.scheduledAt)}</td>
                      <td>
                        <span className={`badge ${STATUS_TONE[v.status] ?? ''}`}>
                          {VISIT_STATUS_LABEL_TH[v.status] ?? v.status}
                        </span>
                      </td>
                      <td className="small">
                        {v.outcome ? (VISIT_OUTCOME_LABEL_TH[v.outcome] ?? v.outcome) : '—'}
                      </td>
                      <td>
                        {v.locationFlag && v.locationFlag !== 'ok' ? (
                          <span className="badge red">
                            {LOCATION_FLAG_LABEL_TH[v.locationFlag] ?? v.locationFlag}
                          </span>
                        ) : v.locationFlag === 'ok' ? (
                          <span className="badge green">ปกติ</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{v.photoCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {cursor && (
              <div style={{ marginTop: 14 }}>
                <button className="secondary" onClick={() => void load(true, cursor)}>
                  โหลดเพิ่ม
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
